import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { AlertCircle, AlertTriangle, Info } from "lucide-react";
import { Alert, AlertDescription } from "../../../components/ui/alert";
import { cn } from "../../../lib/utils";

/**
 * Everything Vista's read has to say about the documents on this page:
 * cross-document checks, missing pages, a side uploaded twice, a foreign
 * document, a side still owed.
 *
 * These used to be five differently-styled boxes stacked by whichever
 * condition happened to be written first, and the cross-document checks were
 * an undivided run of `mt-1` rectangles — several separate findings reading as
 * one block of red. Each finding is now its own container, they share one
 * severity vocabulary, and they are ordered worst-first so the thing actually
 * holding Continue is the thing at the top.
 */

const SEVERITY = {
  error: {
    icon: AlertCircle,
    alert: "border-[#F2C3BF] bg-[#FEF6F5] text-[#9E251D]",
    icon_class: "text-[#D93F35]",
  },
  warning: {
    icon: AlertTriangle,
    alert: "border-[#EFD5A3] bg-[#FEFAF0] text-[#845209]",
    icon_class: "text-[#B8741A]",
  },
  info: {
    icon: Info,
    alert: "border-[#CDEAF9] bg-[#F4FAFF] text-[#1F4E63]",
    icon_class: "text-[#0A7CAE]",
  },
};

// Worst first. A customer looking at a disabled Continue button should not
// have to read past two advisory notes to find the one that stopped them.
const SEVERITY_ORDER = ["error", "warning", "info"];

// Vista composes a check's description server-side, and some of them carry
// more than one finding in the one string — the plate that matched nothing,
// then the plates the report did list. Rendered verbatim that is a paragraph
// of separate problems inside a single box, which is the shape this component
// exists to break up: one finding, one row.
//
// Only unambiguous list separators count — a newline or a bullet. Splitting on
// sentences, or on a semicolon, would tear apart the checks whose copy is one
// finding plus its detail ("…may be expected if the owner wasn't driving";
// "plate X not found; the report lists Y, Z"), turning one amber note into two
// and making the detail read as a second thing wrong.
const FINDING_SEPARATOR = /\r?\n+|\s*\u2022\s*/;
// A separator often leaves its list marker behind on the next line.
const LIST_MARKER = /^\s*(?:\d+[.)]|[-\u2013\u2014*\u2022])\s+/;

/**
 * One Vista check's copy, as the rows it should occupy.
 * Always at least one row: a description with no separators in it is
 * returned unchanged.
 *
 * @param {string} text
 * @returns {string[]}
 */
export const splitFindingText = (text) => {
  const whole = String(text || "").trim();
  const parts = whole
    .split(FINDING_SEPARATOR)
    .map((part) => part.replace(LIST_MARKER, "").trim())
    .filter(Boolean);
  return parts.length ? parts : [whole].filter(Boolean);
};

/**
 * One Vista check's copy, as the rows it should occupy.
 *
 * Vista sends `descriptionLines` / `descriptionLinesAr` for exactly this: a
 * four-vehicle plate failure is four findings, and arrives as four strings
 * rather than one paragraph to be broken up by guesswork. The two arrays are
 * index-aligned, and a single-finding check is a one-element array, so there
 * is nothing to branch on.
 *
 * `description` is the same copy joined, and is the fallback for a response
 * from before the lines shipped — split on its list separators, as before.
 *
 * @param {Object} check - one crossValidation check
 * @param {(en: any, ar: any) => any} translate - useLanguageTranslate
 * @returns {string[]}
 */
export const findingLines = (check, translate) => {
  const lines = translate(check?.descriptionLines, check?.descriptionLinesAr);
  if (Array.isArray(lines) && lines.length) {
    return lines.map((line) => String(line).trim()).filter(Boolean);
  }
  return splitFindingText(
    translate(check?.description, check?.descriptionAr) ||
      check?.displayName ||
      check?.checkName
  );
};

// Long enough to read as a cascade, short enough that the last notice is not
// waiting on the first. Capped so a page with six findings does not spend a
// quarter of a second dealing them out.
const STAGGER_STEP = 0.04;
const STAGGER_CAP = 3;

const ValidationNotices = ({ notices = [], className }) => {
  const reduceMotion = useReducedMotion();

  const ordered = [...notices].sort(
    (a, b) =>
      SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity)
  );

  if (ordered.length === 0) return null;

  return (
    <div className={cn("mt-3 space-y-2", className)}>
      {/* initial={false} so notices restored with the page on Back simply
          exist. They animate when Vista's read produces them, which is the
          only time their arrival is news. */}
      <AnimatePresence initial={false}>
        {ordered.map((notice, index) => {
          const severity = SEVERITY[notice.severity] || SEVERITY.info;
          const Icon = severity.icon;
          return (
            <motion.div
              key={notice.key}
              // Position only: a notice clearing in the middle of the stack
              // should let the ones below slide up, without the boxes
              // resizing their own content on the way.
              layout={reduceMotion ? false : "position"}
              initial={{ opacity: 0, y: reduceMotion ? 0 : -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{
                opacity: 0,
                y: reduceMotion ? 0 : -4,
                // Faster out than in: a finding that has been fixed should
                // get out of the way, not take a bow.
                transition: { duration: 0.15, ease: [0.22, 1, 0.36, 1] },
              }}
              transition={{
                duration: 0.24,
                ease: [0.22, 1, 0.36, 1],
                delay: reduceMotion
                  ? 0
                  : Math.min(index, STAGGER_CAP) * STAGGER_STEP,
              }}
            >
              <Alert className={cn("rounded-[10px]", severity.alert)}>
                <Icon
                  className={cn("w-4 h-4 shrink-0 mt-[1px]", severity.icon_class)}
                  aria-hidden="true"
                />
                <AlertDescription className="min-w-0">
                  {notice.text}
                  {notice.details?.length > 0 && (
                    <ul className="mt-1.5 space-y-0.5 text-xs opacity-90">
                      {notice.details.map((detail, i) => (
                        <li key={i} className="break-words">
                          {detail}
                        </li>
                      ))}
                    </ul>
                  )}
                </AlertDescription>
              </Alert>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};

export default ValidationNotices;
